// Was der Schnittbericht-Dialog prüft, sortiert und vorbelegt -- ohne React.
//
// Dieselbe Linie wie `publish.ts`: die Regeln sind die des Modells
// (`app/models/cut_report.rb`), und sie hier zu haben heißt, dass die Nutzerin
// den Fehler sieht, bevor sie auf „Speichern" drückt -- und dass sie in vitest
// belegbar sind, ohne einen Dialog zu mounten. Der Server bleibt die Instanz,
// die entscheidet.
//
// Die Strings stehen in de.ts (`cutReport`), wie im ganzen Editor.

import { t } from '../i18n'

export type DeviceKind = 'cutter' | 'laser' | 'other'
export type CutOutcome = 'good' | 'adjusted' | 'failed'
export type CutDeviceProfile = 'cricut' | 'xtool' | 'other'

export type CatalogDevice = {
  id: number
  name: string
  brand: string
  kind: DeviceKind
}

// Ein eigener Bericht, so wie ihn `GET /api/v1/cut_reports` liefert. Nur die
// Felder, die die Vorbelegung und die Liste im Dialog brauchen.
export type CutReportSummary = {
  id: number
  device_id: number
  device_name: string | null
  material: string
  settings: Record<string, string | number>
  outcome: CutOutcome
  created_at: string
}

export const MATERIAL_MAX_LENGTH = 60
export const NOTES_MAX_LENGTH = 500

export const OUTCOME_OPTIONS: readonly { value: CutOutcome; label: string; hint: string }[] = [
  { value: 'good', label: t('cutReport.outcomeGood'), hint: t('cutReport.outcomeGoodHint') },
  { value: 'adjusted', label: t('cutReport.outcomeAdjusted'), hint: t('cutReport.outcomeAdjustedHint') },
  { value: 'failed', label: t('cutReport.outcomeFailed'), hint: t('cutReport.outcomeFailedHint') },
]

export const DEVICE_PROFILE_OPTIONS: readonly { value: CutDeviceProfile; label: string }[] = [
  { value: 'cricut', label: t('cutReport.profileCricut') },
  { value: 'xtool', label: t('cutReport.profileXtool') },
  { value: 'other', label: t('cutReport.profileOther') },
]

// Die Vorschlagsliste für das `<datalist>`. Sie kommt vom Server
// (`Materials::SUGGESTIONS`); diese Kopie ist nur der Rückfall, solange die
// Antwort noch unterwegs ist.
export const MATERIAL_FALLBACK: readonly string[] = [
  'Vinyl',
  'Flexfolie',
  'Flockfolie',
  'Papier 160 g',
  'Karton 300 g',
  'Sperrholz 3 mm',
  'Sperrholz 4 mm',
  'Acryl 3 mm',
  'Leder',
  'Filz',
  'Balsa',
]

// Die Felder stehen als Zeichenketten im Entwurf, weil sie aus `<input>`
// kommen: eine leere Zahl ist '' und nicht 0, und genau der Unterschied
// entscheidet, ob ein Wert mitgeschickt wird.
export type CutReportDraft = {
  deviceId: string
  material: string
  outcome: CutOutcome | ''
  notes: string
  blade: string
  pressure: string
  preset: string
  powerPct: string
  speedMmS: string
  passes: string
}

export type CutReportErrors = Partial<
  Record<'deviceId' | 'material' | 'outcome' | 'notes' | 'pressure' | 'powerPct' | 'speedMmS' | 'passes', string>
>

export function emptyDraft(): CutReportDraft {
  return {
    deviceId: '',
    material: '',
    outcome: '',
    notes: '',
    blade: '',
    pressure: '',
    preset: '',
    powerPct: '',
    speedMmS: '',
    passes: '',
  }
}

/**
 * Welche Einstellungsfelder zu einem Gerät gehören.
 *
 * Ein Laser hat keinen Anpressdruck und eine Schneidemaschine keine Leistung
 * in Prozent; ein Formular, das beides zeigt, ist eines, in dem die Hälfte der
 * Felder rät. `other` zeigt beides -- das ist das Gerät, von dem wir nichts
 * wissen.
 */
export function settingsKindOf(devices: readonly CatalogDevice[], deviceId: string): DeviceKind | null {
  const id = Number(deviceId)
  if (!Number.isFinite(id) || id <= 0) return null

  return devices.find((device) => device.id === id)?.kind ?? null
}

export function showsCutterSettings(kind: DeviceKind | null): boolean {
  return kind === 'cutter' || kind === 'other' || kind === null
}

export function showsLaserSettings(kind: DeviceKind | null): boolean {
  return kind === 'laser' || kind === 'other' || kind === null
}

// Eine Zahl aus einem Feld -- oder `null`, wenn dort keine steht. `Number('')`
// ist 0, und eine 0 wäre hier eine Angabe.
function numberOf(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null

  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

function checkInteger(
  errors: CutReportErrors,
  key: 'pressure' | 'passes',
  value: string,
  min: number,
  max: number,
  label: string,
): void {
  const parsed = numberOf(value)
  if (parsed === null) return

  if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
    errors[key] = t('cutReport.validation.integerRange', { label, min, max })
  }
}

export function validateCutReport(draft: CutReportDraft, kind: DeviceKind | null): CutReportErrors {
  const errors: CutReportErrors = {}
  const material = draft.material.trim()

  if (draft.deviceId.trim() === '') errors.deviceId = t('cutReport.validation.device')

  if (material === '') errors.material = t('cutReport.validation.material')
  else if (material.length > MATERIAL_MAX_LENGTH) {
    errors.material = t('cutReport.validation.maxLength', { max: MATERIAL_MAX_LENGTH })
  }

  if (draft.outcome === '') errors.outcome = t('cutReport.validation.outcome')

  if (draft.notes.trim().length > NOTES_MAX_LENGTH) {
    errors.notes = t('cutReport.validation.maxLength', { max: NOTES_MAX_LENGTH })
  }

  if (showsCutterSettings(kind)) {
    checkInteger(errors, 'pressure', draft.pressure, 1, 500, t('cutReport.pressure'))
  }

  if (showsLaserSettings(kind)) {
    const power = numberOf(draft.powerPct)
    if (power !== null && (Number.isNaN(power) || power < 0 || power > 100)) {
      errors.powerPct = t('cutReport.validation.power')
    }

    const speed = numberOf(draft.speedMmS)
    if (speed !== null && (Number.isNaN(speed) || speed <= 0 || speed > 10_000)) {
      errors.speedMmS = t('cutReport.validation.speed')
    }

    checkInteger(errors, 'passes', draft.passes, 1, 10, t('cutReport.passes'))
  }

  return errors
}

export function isReportable(draft: CutReportDraft, kind: DeviceKind | null): boolean {
  return Object.keys(validateCutReport(draft, kind)).length === 0
}

// Nur die Felder, die zum Gerät gehören und in denen etwas steht. Ein leeres
// Feld mitzuschicken hieße, eine Null zu behaupten, die niemand eingestellt
// hat.
export function settingsPayload(draft: CutReportDraft, kind: DeviceKind | null): Record<string, string> {
  const settings: Record<string, string> = {}

  if (showsCutterSettings(kind)) {
    if (draft.blade.trim() !== '') settings.blade = draft.blade.trim()
    if (draft.pressure.trim() !== '') settings.pressure = draft.pressure.trim()
    if (draft.preset.trim() !== '') settings.preset = draft.preset.trim()
  }

  if (showsLaserSettings(kind)) {
    if (draft.powerPct.trim() !== '') settings.power_pct = draft.powerPct.trim()
    if (draft.speedMmS.trim() !== '') settings.speed_mm_s = draft.speedMmS.trim()
    if (draft.passes.trim() !== '') settings.passes = draft.passes.trim()
  }

  return settings
}

// Was der Editor über den Schnitt weiß, ohne zu fragen: das Exportprofil und
// die Breite des letzten Exports, das Projekt und die Vorlage, aus der es
// stammt.
export type CutReportContext = {
  projectId: number
  templateId: number | null
  deviceProfile: CutDeviceProfile
  widthMm: number | null
  exportFormat: string | null
}

/**
 * Der Rumpf für `POST /api/v1/cut_reports`.
 *
 * `FormData` und kein JSON, weil ein Foto mitkommen darf. Die Schlüssel sind
 * die des Servers (snake_case), nicht die des Formulars.
 */
export function cutReportFormData(
  draft: CutReportDraft,
  kind: DeviceKind | null,
  context: CutReportContext,
  photo: File | null,
): FormData {
  const form = new FormData()

  form.set('device_id', draft.deviceId)
  form.set('material', draft.material.trim())
  form.set('outcome', draft.outcome)
  form.set('device_profile', context.deviceProfile)
  form.set('project_id', String(context.projectId))
  if (context.templateId !== null) form.set('template_id', String(context.templateId))
  if (context.widthMm !== null) form.set('width_mm', String(context.widthMm))
  if (context.exportFormat !== null) form.set('export_format', context.exportFormat)
  if (draft.notes.trim() !== '') form.set('notes', draft.notes.trim())

  for (const [key, value] of Object.entries(settingsPayload(draft, kind))) {
    form.set(`settings[${key}]`, value)
  }

  if (photo) form.set('photo', photo)

  return form
}

/**
 * Der letzte eigene Bericht zu diesem Gerät und diesem Material.
 *
 * Das ist die Vorbelegung, die den Dialog nützlich macht: wer zum dritten Mal
 * Flexfolie auf dem Maker schneidet, soll die Klinge und den Anpressdruck
 * nicht wieder eintippen. Verglichen wird das Material getrimmt und ohne
 * Rücksicht auf Groß- und Kleinschreibung -- genauso, wie das Modell es
 * vergleicht.
 *
 * Die Liste kommt schon nach Datum sortiert (neueste zuerst); gesucht wird
 * deshalb der erste Treffer und nicht der beste.
 */
export function lastReportFor(
  reports: readonly CutReportSummary[],
  deviceId: string,
  material: string,
): CutReportSummary | null {
  const id = Number(deviceId)
  const key = material.trim().toLowerCase()
  if (!Number.isFinite(id) || id <= 0 || key === '') return null

  return reports.find((report) => report.device_id === id && report.material.trim().toLowerCase() === key) ?? null
}

// Die sechs Einstellungsfelder, die eine Vorbelegung anfasst -- und nur die:
// Material und Gerät stehen ja schon da, und das Ergebnis ist die eine Frage,
// die niemand für die Nutzerin beantworten darf.
export type PrefilledSettings = Pick<
  CutReportDraft,
  'blade' | 'pressure' | 'preset' | 'powerPct' | 'speedMmS' | 'passes'
>

export const NO_PREFILLED_SETTINGS: PrefilledSettings = {
  blade: '',
  pressure: '',
  preset: '',
  powerPct: '',
  speedMmS: '',
  passes: '',
}

// Was ein früherer Bericht für diese sechs Felder sagt, als Text.
export function reportSettings(report: CutReportSummary | null): PrefilledSettings {
  if (report === null) return { ...NO_PREFILLED_SETTINGS }

  const text = (key: string): string => {
    const value = report.settings[key]
    return value === undefined || value === null ? '' : String(value)
  }

  return {
    blade: text('blade'),
    pressure: text('pressure'),
    preset: text('preset'),
    powerPct: text('power_pct'),
    speedMmS: text('speed_mm_s'),
    passes: text('passes'),
  }
}

/**
 * Die Einstellungen aus einem früheren Bericht in die Felder des Entwurfs.
 *
 * `filled` ist, was dieselbe Vorbelegung beim letzten Mal geschrieben hat.
 * Nur ein leeres Feld und ein Feld, das noch genau so dasteht, werden
 * ersetzt -- eine Zahl, die die Nutzerin selbst getippt hat, bleibt stehen.
 * Ohne diese Unterscheidung schnappte der Druck auf den alten Wert zurück,
 * sobald das Materialfeld ein bekanntes Paar wieder traf.
 */
export function prefillSettings(
  draft: CutReportDraft,
  report: CutReportSummary | null,
  filled: PrefilledSettings = NO_PREFILLED_SETTINGS,
): CutReportDraft {
  if (report === null) return draft

  const next = reportSettings(report)
  const keys = Object.keys(NO_PREFILLED_SETTINGS) as (keyof PrefilledSettings)[]
  const updated = { ...draft }

  for (const key of keys) {
    if (draft[key] === '' || draft[key] === filled[key]) updated[key] = next[key]
  }

  return updated
}

/**
 * Die Geräteauswahl: die eigenen zuerst, danach der Katalog nach Marke
 * gruppiert.
 *
 * „Meine Geräte" ist keine Bequemlichkeit, sondern die wahrscheinlichste
 * Antwort -- wer berichtet, berichtet fast immer über das Gerät, das in seinem
 * Profil steht. Die Marken behalten die Reihenfolge des Katalogs (der Server
 * sortiert nach `position`), damit die Liste im Dialog dieselbe ist wie im
 * Konto.
 */
export type DeviceGroup = { label: string; devices: CatalogDevice[] }

export function deviceGroups(catalog: readonly CatalogDevice[], mine: readonly CatalogDevice[]): DeviceGroup[] {
  const groups: DeviceGroup[] = []
  if (mine.length > 0) groups.push({ label: t('cutReport.myDevices'), devices: [...mine] })

  for (const device of catalog) {
    const existing = groups.find((group) => group.label === device.brand)
    if (existing) existing.devices.push(device)
    else groups.push({ label: device.brand, devices: [device] })
  }

  return groups
}

// Das Gerät, mit dem der Dialog aufgeht: das erste eigene, sonst keins. Raten
// ist hier falsch -- ein falsch zugeordneter Bericht ist schlechter als
// keiner.
export function defaultDeviceId(mine: readonly CatalogDevice[]): string {
  return mine.length > 0 ? String(mine[0].id) : ''
}

// Die Fehler, die der Server zurückgibt, in einem Satz.
export const CUT_REPORT_ERROR_MESSAGES: Record<string, string> = {
  invalid: t('cutReport.errors.invalid'),
  not_found: t('cutReport.errors.not_found'),
  too_many_requests: t('cutReport.errors.too_many_requests'),
}
